// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import * as HolidayCommands from '@app/features/holiday/commands/HolidayCommands';
import styles from '@app/features/holiday/components/ComposerHolidayPeek.module.css';
import {
	type ComposerHolidayPeekPack,
	peekHeightRatio,
	peekLedgeRatio,
	resolveComposerHolidayPeekPack,
} from '@app/features/holiday/components/ComposerHolidayPeekResolvers';
import type {
	ComposerHolidayPeekMotion,
	ComposerHolidayPeekView,
} from '@app/features/holiday/components/ComposerHolidayPeekStateMachine';
import ComposerHolidayPeekSession from '@app/features/holiday/state/ComposerHolidayPeek';
import Theme from '@app/features/theme/state/Theme';
import ThemeLibrary from '@app/features/theme/state/ThemeLibrary';
import {touchAvatarCostumeThemeDeps} from '@app/features/ui/avatars/AvatarCostume';
import {costumeColorNeedsHalo} from '@app/features/ui/avatars/AvatarCostumeContrast';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo, useState} from 'react';

function peekStyleVars(pack: ComposerHolidayPeekPack, visibleHeight: number): React.CSSProperties {
	return {
		'--peek-width': `${pack.crop.widthRem}rem`,
		'--peek-right': `${pack.crop.rightRem}rem`,
		'--peek-ledge-ratio': String(peekLedgeRatio(pack.crop.ledgeUnits, pack.crop.artWidth)),
		'--peek-ledge-extra': `${pack.crop.ledgeExtraPx}px`,
		'--peek-visible-ratio': String(peekHeightRatio(visibleHeight, pack.crop.artWidth)),
		'--peek-pop-ms': `${pack.machineInput.popMs}ms`,
		'--peek-morph-ms': `${pack.machineInput.morphMs}ms`,
		'--peek-hide-ms': `${pack.machineInput.hideMs}ms`,
	} as React.CSSProperties;
}

function visibleStageId(model: ComposerHolidayPeekView): string | null {
	if (model.motion === 'morph' || (model.motion === 'hide' && model.nextStage != null)) {
		return model.nextStage;
	}
	return model.currentStage;
}

/** Only the stages this frame can show: the current pose, plus the one it is morphing (or hiding) into. */
function mountedStageIds(model: ComposerHolidayPeekView): Array<string> {
	const ids: Array<string> = [];
	if (model.currentStage != null) ids.push(model.currentStage);
	if (model.nextStage != null && model.nextStage !== model.currentStage) ids.push(model.nextStage);
	return ids;
}

function ForcedPeekLoop({
	pack,
	needsHalo,
}: {
	pack: ComposerHolidayPeekPack;
	needsHalo: boolean;
}): React.ReactElement | null {
	const ladder = pack.ladder;
	const [motion, setMotion] = useState<ComposerHolidayPeekMotion>('pop');
	const [stageIndex, setStageIndex] = useState(0);
	const timings = pack.machineInput;

	useEffect(() => {
		if (ladder.length === 0) return;
		if (motion === 'pop') {
			const timeoutId = window.setTimeout(() => setMotion('hold'), timings.popMs);
			return () => window.clearTimeout(timeoutId);
		}
		if (motion === 'hold') {
			const timeoutId = window.setTimeout(() => {
				if (stageIndex < ladder.length - 1) {
					setMotion('morph');
					return;
				}
				setMotion('hide');
			}, 2500);
			return () => window.clearTimeout(timeoutId);
		}
		if (motion === 'morph') {
			const timeoutId = window.setTimeout(() => {
				setStageIndex((index) => Math.min(index + 1, ladder.length - 1));
				setMotion('hold');
			}, timings.morphMs);
			return () => window.clearTimeout(timeoutId);
		}
		if (motion === 'hide') {
			const timeoutId = window.setTimeout(() => {
				setStageIndex(0);
				setMotion('idle');
			}, timings.hideMs);
			return () => window.clearTimeout(timeoutId);
		}
		const timeoutId = window.setTimeout(() => setMotion('pop'), 1500);
		return () => window.clearTimeout(timeoutId);
	}, [ladder.length, motion, stageIndex, timings.hideMs, timings.morphMs, timings.popMs]);

	if (motion === 'idle' || ladder.length === 0) return null;

	const currentId = ladder[stageIndex] ?? null;
	const nextId = motion === 'morph' ? (ladder[stageIndex + 1] ?? null) : null;
	const model: ComposerHolidayPeekView = {channelId: null, motion, currentStage: currentId, nextStage: nextId};
	const visibleId = visibleStageId(model);
	const visibleHeight = visibleId != null ? pack.stages[visibleId]?.height : pack.stages[ladder[0]!]?.height;
	if (visibleHeight == null) return null;

	return (
		<div
			className={clsx(styles.anchor, !needsHalo && styles.noHalo)}
			style={peekStyleVars(pack, visibleHeight)}
			data-motion={motion}
			data-flx="holiday.composer-holiday-peek"
			aria-hidden="true"
		>
			<div
				className={styles.slide}
				key={`${currentId ?? 'none'}${motion === 'pop' ? '-pop' : motion === 'hide' ? '-hide' : ''}`}
				data-flx="holiday.composer-holiday-peek.forced-peek-loop.slide"
			>
				{mountedStageIds(model).map((stageId) => {
					const stage = pack.stages[stageId];
					if (stage == null) return null;
					const Svg = stage.Svg;
					return (
						<Svg
							key={stageId}
							className={styles.stage}
							data-visible={visibleStageId(model) === stageId ? 'true' : 'false'}
							data-flx={`holiday.composer-holiday-peek.stage.${stageId}`}
						/>
					);
				})}
			</div>
		</div>
	);
}

/**
 * Right-side composer peek (Holiday Peeks). Mount as the first child of
 * `flx-channel-textarea`. Do not put this in HolidayLayer.
 */
export const ComposerHolidayPeek = observer(function ComposerHolidayPeek({
	channelId,
}: {
	channelId: string;
}): React.ReactElement | null {
	const pack = resolveComposerHolidayPeekPack();
	const model = ComposerHolidayPeekSession.model;
	touchAvatarCostumeThemeDeps();
	const bodyColor = pack?.stages[pack.ladder[0] ?? '']?.bodyColor ?? '#121414';
	const needsHalo = useMemo(
		() => costumeColorNeedsHalo(bodyColor),
		[bodyColor, Theme.effectiveTheme, Accessibility.customThemeCss, ThemeLibrary.revision],
	);

	useEffect(() => {
		return () => {
			HolidayCommands.resetComposerHolidayPeek(channelId);
		};
	}, [channelId]);

	if (Accessibility.useReducedMotion || pack == null) return null;
	if (pack.forceArtLoop?.()) {
		return (
			<ForcedPeekLoop pack={pack} needsHalo={needsHalo} data-flx="holiday.composer-holiday-peek.forced-peek-loop" />
		);
	}
	if (model == null || model.channelId !== channelId) return null;

	const visibleId = visibleStageId(model);
	const visibleHeight = visibleId != null ? pack.stages[visibleId]?.height : null;
	if (visibleHeight == null) return null;

	return (
		<div
			className={clsx(styles.anchor, !needsHalo && styles.noHalo)}
			style={peekStyleVars(pack, visibleHeight)}
			data-motion={model.motion}
			data-flx="holiday.composer-holiday-peek"
			aria-hidden="true"
		>
			<div
				className={styles.slide}
				key={`${model.currentStage ?? visibleId ?? 'none'}${model.motion === 'pop' ? '-pop' : model.motion === 'hide' ? '-hide' : ''}`}
				data-flx="holiday.composer-holiday-peek.slide"
			>
				{mountedStageIds(model).map((stageId) => {
					const stage = pack.stages[stageId];
					if (stage == null) return null;
					const Svg = stage.Svg;
					return (
						<Svg
							key={stageId}
							className={styles.stage}
							data-visible={visibleStageId(model) === stageId ? 'true' : 'false'}
							data-flx={`holiday.composer-holiday-peek.stage.${stageId}`}
						/>
					);
				})}
			</div>
		</div>
	);
});
